import showToast from './toast';

const LABELS = [
  [/\/masters\/customers/, 'Customer'],
  [/\/masters\/services/, 'Service'],
  [/\/masters\/sectors/, 'Sector'],
  [/\/masters\/tender-types/, 'Tender type'],
  [/\/masters\/sources/, 'Source'],
  [/\/knowledge\/tender-credentials/, 'Credential on tender'],
  [/\/knowledge\/credentials/, 'Credential'],
  [/\/knowledge\/projects/, 'Project experience'],
  [/\/knowledge\/reusable/, 'Reusable content'],
  [/\/personnel\/proposals/, 'Person on tender'],
  [/\/personnel/, 'Personnel'],
  [/\/timesheets/, 'Timesheet'],
  [/\/rates/, 'Rate'],
  [/\/roles\/templates/, 'Role'],
  [/\/roles/, 'Role'],
  [/\/parivartan\/users|\/users/, 'User'],
  [/\/locations/, 'Location'],
  [/\/departments/, 'Department'],
  [/\/parivartan\/company/, 'Company'],
  [/\/ai-config/, 'AI settings'],
  [/\/support\/tickets|\/parivartan\/support\/tickets/, 'Support ticket'],
  [/\/support\/faqs/, 'FAQ'],
  [/\/approvals\/work-items/, 'Approval'],
  [/\/cost-estimates/, 'Cost estimate'],
  [/\/assignments/, 'Assignment'],
  [/\/eligibility/, 'Eligibility'],
  [/\/decision/, 'Decision'],
  [/\/technical-sections/, 'Technical section'],
  [/\/submission/, 'Submission'],
  [/\/award/, 'Award'],
  [/\/documents/, 'Document'],
  [/\/onboarding\/step/, 'Onboarding'],
  [/\/onboarding\/activate/, 'Workspace'],
  [/\/calendar\/events/, 'Calendar event'],
  [/\/evaluations/, 'Evaluation'],
  [/\/issued-rfps/, 'RFP'],
  [/\/pre-qualification/, 'Vendor'],
  [/\/market-declarations/, 'Declaration'],
  [/\/regulatory-compliance\/certificates/, 'Certificate'],
  [/\/regulatory-compliance\/vendors/, 'Vendor'],
  [/\/regulatory-compliance/, 'Declaration'],
  [/\/competitors/, 'Competitor'],
  [/\/sources-watchlists\/watchlists/, 'Watchlist'],
  [/\/sources-watchlists\/sources/, 'Source'],
  [/\/watchlists/, 'Watchlist'],
  [/\/sources/, 'Source'],
  [/\/tenders\/[^/]+\/notes|\/notes$/, 'Note'],
  [/\/pipeline-stage/, 'Tender'],
  [/\/tenders/, 'Tender'],
  [/\/work-items/, 'Work item']
];

const UPSERT = /\/(personnel|rates|locations|departments|eligibility|decision)(\/|$)|\/knowledge\/|\/roles\/templates|\/onboarding\/step/;

const SKIP = /\/auth(\/|$)|\/ai\/sessions|\/ask$|\/ai-config\/test|\/seed|\/scan|\/simulate|\/lookup|\/masters\/upload(?:\/|$)|\/validate(?:-all)?(?:\/|$)/;

const ACTION = {
  deactivate: 'deactivated',
  submit: 'submitted',
  approve: 'approved',
  return: 'returned',
  publish: 'published',
  review: 'reviewed',
  activate: 'activated',
  restore: 'restored',
  classify: 'classified'
};

const requestPath = (config) => {
  const base = String(config?.baseURL || '');
  const relative = String(config?.url || '');
  const combined = relative
    ? `${base.replace(/\/+$/, '')}/${relative.replace(/^\/+/, '')}`
    : base;
  try {
    return new URL(combined, 'http://localhost').pathname;
  } catch {
    return combined.split('?')[0];
  }
};

const labelFor = (path) => {
  const match = LABELS.find(([pattern]) => pattern.test(path));
  if (match) return match[1];
  const parts = path.split('/').filter(Boolean);
  const word = [...parts].reverse().find((part) => !/^[0-9a-f-]{8,}$/i.test(part) && !/^\d+$/.test(part));
  if (!word) return 'Record';
  const text = word.replace(/[-_]/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const verbFor = (method, path) => {
  const action = path.match(/\/(deactivate|submit|approve|return|publish|review|activate|restore|classify)(\/|$)/);
  if (action) return ACTION[action[1]];
  if (/\/process(-ai)?$/.test(path)) return 'sent for processing';
  if (method === 'delete') return 'deleted';
  if (method === 'put' || method === 'patch') return 'updated';
  if (UPSERT.test(path)) return 'saved';
  return 'created';
};

const shouldSkip = (config) => {
  if (!config || config.skipToast) return true;
  const method = String(config.method || 'get').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(method)) return true;
  if (config.responseType === 'blob') return true;
  const path = requestPath(config);
  if (SKIP.test(path)) return true;
  if (/\/(test|run|sync)$/.test(path)) return true;
  if (/\/support\//.test(path) && !/\/parivartan\/support\//.test(path)) return true;
  return false;
};

const DETAIL = {
  created: (name, label) => (name ? `${name} has been added.` : `A new ${label.toLowerCase()} is on the master.`),
  updated: (name, label) => (name ? `${name} is up to date.` : `Your changes to this ${label.toLowerCase()} are saved.`),
  deleted: (name, label) => (name ? `${name} has been removed.` : `This ${label.toLowerCase()} has been removed.`),
  saved: (name, label) => (name ? `${name} has been saved.` : `This ${label.toLowerCase()} has been saved.`),
  submitted: (name) => (name ? `${name} has been submitted.` : 'It has been submitted.'),
  approved: (name) => (name ? `${name} has been approved.` : 'It has been approved.'),
  returned: (name) => (name ? `${name} has been returned.` : 'It has been returned.'),
  deactivated: (name) => (name ? `${name} is now inactive.` : 'It is now inactive.'),
  published: (name) => (name ? `${name} has been published.` : 'It has been published.'),
  reviewed: (name) => (name ? `${name} has been reviewed.` : 'The review has been saved.'),
  activated: (name) => (name ? `${name} is now active.` : 'It is now active.'),
  restored: (name) => (name ? `${name} has been restored.` : 'It has been restored.'),
  classified: (name) => (name ? `${name} has been classified.` : 'The classification has been saved.'),
  'sent for processing': (name) => (name ? `${name} has been sent for processing.` : 'Processing has started.')
};

const TITLE = {
  created: (label) => `${label} added`,
  updated: (label) => `${label} updated`,
  deleted: (label) => `${label} removed`,
  saved: (label) => `${label} saved`,
  submitted: (label) => `${label} submitted`,
  approved: (label) => `${label} approved`,
  returned: (label) => `${label} returned`,
  deactivated: (label) => `${label} deactivated`,
  published: (label) => `${label} published`,
  reviewed: (label) => `${label} reviewed`,
  activated: (label) => `${label} activated`,
  restored: (label) => `${label} restored`,
  classified: (label) => `${label} classified`,
  'sent for processing': () => 'Processing started'
};

const COULD_NOT = {
  created: 'add',
  updated: 'update',
  deleted: 'remove',
  saved: 'save',
  submitted: 'submit',
  approved: 'approve',
  returned: 'return',
  deactivated: 'deactivate',
  published: 'publish',
  reviewed: 'review',
  activated: 'activate',
  restored: 'restore',
  classified: 'classify',
  'sent for processing': 'process'
};

const pickName = (value, depth = 0) => {
  if (!value || depth > 4 || typeof value !== 'object' || Array.isArray(value)) return '';
  if (typeof value.name === 'string' && value.name.trim()) return value.name.trim();
  if (typeof value.title === 'string' && value.title.trim()) return value.title.trim();
  if (typeof value.shortName === 'string' && value.shortName.trim()) return value.shortName.trim();
  const nested = ['service', 'customer', 'sector', 'tenderType', 'source', 'tender', 'personnel', 'rate', 'timesheet', 'user', 'ticket', 'data'];
  for (const key of nested) {
    if (value[key]) {
      const found = pickName(value[key], depth + 1);
      if (found) return found;
    }
  }
  return '';
};

const readJson = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return null; }
  }
  if (typeof FormData !== 'undefined' && value instanceof FormData) return null;
  return typeof value === 'object' ? value : null;
};

const noticeFor = (config, body) => {
  if (typeof config?.toast === 'string' && config.toast) return { title: config.toast, detail: '' };
  const path = requestPath(config);
  const method = String(config.method || 'post').toLowerCase();
  let verb = verbFor(method, path);
  let label = labelFor(path);
  if (/\/approvals\/work-items\/[^/]+\/action/.test(path)) {
    const payload = readJson(config.data);
    const action = String(payload?.action || payload?.decision || '').toLowerCase();
    label = 'Approval';
    if (action.includes('reject')) verb = 'returned';
    else if (action.includes('return')) verb = 'returned';
    else if (action.includes('approv')) verb = 'approved';
    else verb = 'updated';
    if (action.includes('reject')) {
      return { title: 'Approval rejected', detail: 'The item has been sent back.' };
    }
    if (action.includes('approv')) {
      return { title: 'Approval granted', detail: 'The item has been approved.' };
    }
  }
  const name = pickName(body) || pickName(readJson(config?.data));
  const title = (TITLE[verb] || ((item) => `${item} ${verb}`))(label);
  const detail = (DETAIL[verb] || ((record) => record || ''))(name, label);
  return { title, detail };
};

const failureFor = (config, serverMessage) => {
  const path = requestPath(config || {});
  const method = String(config?.method || 'post').toLowerCase();
  const verb = verbFor(method, path);
  const label = labelFor(path).toLowerCase();
  const action = COULD_NOT[verb] || 'save';
  return {
    title: `Couldn't ${action} this ${label}`,
    detail: serverMessage || 'Please try again.'
  };
};

export function attachCrudToasts(api) {
  if (!api || api.__crudToastAttached) return api;
  api.__crudToastAttached = true;
  api.interceptors.response.use(
    (response) => {
      const config = response?.config;
      if (!shouldSkip(config)) {
        const body = response?.data;
        if (body && body.success === false) {
          showToast.error(failureFor(config, body.message));
        } else {
          showToast.success(noticeFor(config, body));
        }
      }
      return response;
    },
    (error) => {
      const config = error?.config;
      const status = error?.response?.status;
      if (!shouldSkip(config) && status !== 401 && error?.code !== 'ERR_CANCELED') {
        const serverMessage = error?.response?.data?.message || error?.response?.data?.error;
        showToast.error(failureFor(config, serverMessage));
      }
      return Promise.reject(error);
    }
  );
  return api;
}
